<template>
  <section class="reviews-section">
    <v-container class="d-flex justify-space-between">
      <h3 class="text-h5 mb-2 text-md-h4 mb-md-0">
        Руководители и врачи о МедТочке
      </h3>
      <div v-if="$display.mdAndUp.value">
        <v-btn
          class="bg-ui-kit-bg-primary btn-rounded-default mr-4"
          icon
          @click="handleSliderButtonClick('left')"
        >
          <v-icon color="primary">ui-icon-arrow-left</v-icon>
        </v-btn>
        <v-btn
          class="bg-ui-kit-bg-primary btn-rounded-default"
          icon
          @click="handleSliderButtonClick('right')"
        >
          <v-icon color="primary">ui-icon-arrow-right</v-icon>
        </v-btn>
      </div>
    </v-container>

    <BaseSlider
      ref="slider"
      class="mt-8 mt-md-12 px-4"
      @scroll:slider="throttledScroll"
    >
      <div
        v-for="(item, index) in managersReviews"
        :key="`slider-item_${index + 1}`"
        class="base-slider-item mx-2 mx-md-3"
        :class="{
          'slider-item_first ml-0': index === 0,
          'slider-item_last mr-0': index === managersReviews.length - 1
        }"
      >
        <v-sheet
          color="ui-kit-bg-secondary"
          class="rounded-xl px-6 py-8"
          elevation="0"
          :height="$display.mdAndUp.value ? 585 : '100%'"
          :width="$display.mdAndUp.value ? 840 : 'calc(100vw - 32px)'"
        >
          <v-sheet
            class="d-flex align-start bg-ui-kit-bg-secondary mb-6 mb-md-8 flex-column flex-md-row"
            :height="$display.mdAndUp.value ? 102 : undefined"
          >
            <v-img
              class="mr-6"
              min-width="80"
              max-width="80"
              height="80"
              alt="Аватар врача"
              :src="item.src"
            />
            <div>
              <div class="mb-1 mt-4 mt-md-0 text-subtitle-1 text-md-h6">{{ item.name }}</div>
              <div
                class="text-ui-kit-text-secondary mb-1"
                :class="$display.mdAndUp.value ? 'text-landing-body-2' : 'text-body-2'"
              >
                {{ item.position }}
              </div>
              <div
                class="d-flex align-center"
                :class="$display.mdAndUp.value ? 'text-landing-body-2' : 'text-body-2'"
              >
                <NuxtLink
                  class="reviews-section__lpu-link text-ui-kit-text-secondary"
                  target="_blank"
                  :to="item.link"
                >
                  {{ item.lpu }}
                </NuxtLink>
                <v-icon
                  size="20"
                  class="ml-1"
                  color="ui-kit-text-secondary"
                >
                  ui-icon-link-2
                </v-icon>
              </div>
            </div>
          </v-sheet>

          <v-divider
            v-if="$display.smAndDown.value"
            class="mb-6"
          />

          <component
            :is="item.reviewComponent"
            class="text-body-2 text-md-landing-body-1"
          />
        </v-sheet>
      </div>
    </BaseSlider>

    <div
      v-if="$display.smAndDown.value"
      class="d-flex justify-center mt-8"
    >
      <div
        v-for="i in 4"
        :key="i"
        class="reviews-section__slider-dot"
        :class="{
          'reviews-section__slider-dot_active': activeSlideIndex === i - 1,
          'mr-3': i < 4
        }"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { throttle } from 'lodash-es'
import { useDisplay } from 'vuetify'
import KlinikaLecheniyaBoliReview
  from '~/features/Home/components/FeaturesSection/ClinicFeatures/Reviews/KlinikaLecheniyaBoliReview.vue'
import SibirMedReview from '~/features/Home/components/FeaturesSection/ClinicFeatures/Reviews/SibirMedReview.vue'
import EvvroLPSReview from '~/features/Home/components/FeaturesSection/ClinicFeatures/Reviews/EvvroLPSReview.vue'
import MediksProfiReview from '~/features/Home/components/FeaturesSection/ClinicFeatures/Reviews/MediksProfiReview.vue'

const slider = ref<HTMLDivElement & { handleDirectionScroll: (direction: 'right' | 'left') => void } | null>(null)
const sliderElement = ref<HTMLElement | null>(null)

const activeSlideIndex = ref(0)

const doctorImagePath = '/images/features/clinic/doctor_review_avatar'
const DEFAULT_CONTAINER_WIDTH = 1136
const managersReviews = [
  {
    src: `${doctorImagePath}_1.png`,
    name: 'Галайчиев Расул Джабраилович',
    position: 'Руководитель, заместитель главного врача',
    lpu: '«Клиника лечения боли», Тюмень',
    link: 'https://prodoctorov.ru/tyumen/lpu/78708-klinika-lecheniya-boli/',
    reviewComponent: KlinikaLecheniyaBoliReview,
  },
  {
    src: `${doctorImagePath}_2.png`,
    name: 'Шконда Дмитрий Сергеевич',
    position: 'Генеральный директор и основатель клиники СибирьМед, врач дерматовенеролог, уролог-андролог',
    lpu: 'Клиника «СибирьМед», Иркутск',
    link: 'https://prodoctorov.ru/irkutsk/lpu/82966-sibirmed/',
    reviewComponent: SibirMedReview,
  },
  {
    src: `${doctorImagePath}_3.png`,
    name: 'Романова Татьяна Александровна',
    position: 'Врач УЗИ',
    lpu: 'Клиника «ЕВВРО ЛПС», Краснодар',
    link: 'https://prodoctorov.ru/krasnodar/lpu/21240-evvro/',
    reviewComponent: EvvroLPSReview,
  },
  {
    src: `${doctorImagePath}_4.png`,
    name: 'Шмидт Евгения Александровна',
    position: 'Директор, главный врач, д.м.н, доцент',
    lpu: 'Медицинский центр «Медикс профи», Кемерово',
    link: 'https://prodoctorov.ru/kemerovo/lpu/76142-mediks-profi/',
    reviewComponent: MediksProfiReview,
  },
]

const $display = useDisplay()

onMounted(() => {
  const firstSlide = document.querySelector('.slider-item_first') as HTMLElement
  const lastSlide = document.querySelector('.slider-item_last') as HTMLElement
  sliderElement.value = document.querySelector('.base-slider__content')

  if (firstSlide) {
    // NOTE: 12px - padding
    firstSlide.style.paddingLeft = `${(window.innerWidth - DEFAULT_CONTAINER_WIDTH) / 2 - 12}px`
  }

  if (lastSlide) {
    // NOTE: 12px - padding
    lastSlide.style.paddingRight = `${(window.innerWidth - DEFAULT_CONTAINER_WIDTH) / 2 - 12}px`
  }
})

const throttledScroll = throttle(handleScroll, 50)

function handleScroll() {
  if (!sliderElement.value || $display.mdAndUp.value) {
    return
  }

  const sliderItemWidth = sliderElement.value.scrollWidth / managersReviews.length
  activeSlideIndex.value = Number((sliderElement.value.scrollLeft / sliderItemWidth).toFixed())
}

function handleSliderButtonClick(direction: 'left' | 'right') {
  slider.value?.handleDirectionScroll(direction)
}
</script>

<style lang="scss" scoped>
.reviews-section {
  &__lpu-link {
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &__slider-dot {
    height: 8px;
    width: 8px;
    background-color: #C4C4C4;
    border-radius: 50%;

    &_active {
      background-color: $t-primary;
    }
  }
}

.btn-rounded-default {
  border-radius: 12px;
}
</style>
